import { RestAPI } from "./rest-api.service";
import { RelatedVideosResponse, Video, VideoPlaybackSource, VideoResponse } from "../types/video.types";
import { BASE_URL } from "../types/constants";

const api = new RestAPI();

export type VideoSearchSort = 'newest' | 'oldest';
export type VideoPlaybackQuality = 'mobile';

export interface VideoSearchFilters {
    sort?: VideoSearchSort;
    event?: string;
    fighter?: string;
    venue?: string;
    type?: string;
    year?: number;
    decade?: number;
}

export const searchVideos = async (
    searchTerm: string,
    page: number = 1,
    pageSize: number = 50,
    tags?: string[],
    filters?: VideoSearchFilters
): Promise<VideoResponse> => {
    const params = new URLSearchParams();
    if (searchTerm && searchTerm.trim().length > 0) {
        params.set('q', searchTerm.trim());
    }
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    if (tags && tags.length > 0) {
        tags.forEach((tag) => params.append('tags', tag));
    }
    if (filters?.sort && filters.sort !== 'newest') {
        params.set('sort', filters.sort);
    }
    if (filters?.event?.trim()) {
        params.set('event', filters.event.trim());
    }
    if (filters?.fighter?.trim()) {
        params.set('fighter', filters.fighter.trim());
    }
    if (filters?.venue?.trim()) {
        params.set('venue', filters.venue.trim());
    }
    if (filters?.type?.trim()) {
        params.set('type', filters.type.trim());
    }
    if (filters?.year) {
        params.set('year', filters.year.toString());
    }
    if (filters?.decade) {
        params.set('decade', filters.decade.toString());
    }
    const url = BASE_URL + `/v1/video/search?${params.toString()}`;
    return await api.get(url);
}

export const fetchVideoDetails = async (videoUri: string): Promise<Video> =>{
    const encodedVideoUri = encodeURIComponent(videoUri);
    const url = BASE_URL + `/v1/video/details?v=${encodedVideoUri}`;
    return await api.get(url);
}

export const watchVideoUrl =  (videoUri: string, quality?: VideoPlaybackQuality): string => {
    const encodedVideoUri = encodeURIComponent(videoUri);
    const qualityQuery = quality ? `&quality=${encodeURIComponent(quality)}` : '';
    const url = BASE_URL + `/v1/video/watch?v=${encodedVideoUri}${qualityQuery}`;
    return url;
}

/**
 * Fetches the authorized playback source URL for the current user.
 */
export const fetchVideoPlaybackSource = async (
    videoUri: string,
    quality?: VideoPlaybackQuality,
    token?: string | null
): Promise<VideoPlaybackSource> => {
    const encodedVideoUri = encodeURIComponent(videoUri);
    const qualityQuery = quality ? `&quality=${encodeURIComponent(quality)}` : '';
    const url = BASE_URL + `/v1/video/playback-source?v=${encodedVideoUri}${qualityQuery}`;
    return await api.get(url, token);
}

export const resolvePlaybackQualityRequest = (
    quality: VideoPlaybackQuality | undefined,
    isMobileVideoPlaybackEnabled: boolean,
    mobilePlaybackOverride?: boolean | null
): VideoPlaybackQuality | undefined => (
    (mobilePlaybackOverride ?? isMobileVideoPlaybackEnabled) ? quality : undefined
);

/**
 * Related videos for a given video.
 * Endpoint: GET /v1/video/related?v={videoUri}&limit={limit}
 */
export const fetchRelatedVideos = async (videoUri: string, limit: number = 12): Promise<RelatedVideosResponse> => {
    const url = BASE_URL + `/v1/video/related?v=${videoUri}&limit=${limit}`;
    return await api.get(url);
}
